import { lazy, Suspense, useEffect } from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import { Routes, Route, BrowserRouter } from 'react-router'
import WebFont from 'webfontloader'
import { ErrorBoundary } from 'react-error-boundary'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DndProvider } from 'react-dnd'
import SnackbarProvider from '~/app/providers/SnackbarProvider'

import { createStore } from '~/app/store/configureStore'
import Login from '~/features/login'
import { ProtectedRoute } from '~/app/Router/ProtectedRoute'
import Root from '~/app/Root'
import { ErrorScreen, Loader } from '~/shared/ui'
import { ApiProvider } from './providers/ApiProvider'
import { Auth0Provider } from './providers/Auth0Provider'
import { ThemeProvider } from './providers/ThemeProvider/ThemeProvider'
import { CheckForUpdates } from '~/app/checkForUpdates'
import { ActiveVideoVisitProvider } from '~/app/providers/ActiveVideoVisitProvider'
import { useInitDatadog } from '~/shared/lib/datadog'
import { datadogLogs } from '@datadog/browser-logs'
import { configureApi } from '@fireflyhealth/api'

// Configure @fireflyhealth/api as early as possible in the application
// lifecycle.
configureApi({
  logger: console,
  axios: {
    baseUrl: import.meta.env.VITE_BASE_URL,
    getToken: () => Promise.resolve(sessionStorage.getItem('sessionToken')),
    logRequests: import.meta.env.DEV,
  },
})

// Lazy load heavy route components for code splitting
const Interface = lazy(() => import('~/app/Interface/Interface'))
const FullscreenVideo = lazy(() => import('~/app/FullscreenVideo'))

// Load Roboto font
WebFont.load({
  google: {
    families: ['Roboto:300,400,500,700'],
  },
})

const store = createStore()

const App = () => {
  // Initialize Datadog for RUM and logging
  useInitDatadog()

  // Add event listener to log when network connection changes.
  useEffect(() => {
    const connection = navigator?.connection
    const listener = () => {
      datadogLogs.logger.info(`Network changed: ${connection?.downlink} Mbit/s down`, connection)
    }

    connection?.addEventListener('change', listener)

    // Clean up event listener when component is unmounted.
    return () => {
      connection?.removeEventListener('change', listener)
    }
  }, [])

  // Knowing the time the client thinks it is, can be useful for debugging.
  useEffect(() => {
    // This could fail if Datadog is not fully initialized, e.g., in tests. It should not be fatal.
    try {
      datadogLogs.logger.info(`DateTime: ${new Date().toISOString()}`)
    } catch (e) {
      // eslint-disable-next-line no-console -- the logger reports through Datadog, which is what just failed
      console.error('Could not log client time to DataDog', e)
    }
  }, [])

  return (
    <ErrorBoundary FallbackComponent={ErrorScreen}>
      <ReduxProvider store={store}>
        <ApiProvider>
          <BrowserRouter>
            <ActiveVideoVisitProvider>
              {process.env.NODE_ENV !== 'development' && <CheckForUpdates />}

              <Auth0Provider>
                <ThemeProvider>
                  <SnackbarProvider>
                    <DndProvider backend={HTML5Backend}>
                      <Root />
                      <Suspense fallback={<Loader />}>
                        <Routes>
                          <Route path="/" element={<Login />} />
                          <Route
                            path="/visit/:id"
                            element={
                              <ProtectedRoute>
                                <FullscreenVideo />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/*"
                            element={
                              <ProtectedRoute>
                                <Interface />
                              </ProtectedRoute>
                            }
                          />
                        </Routes>
                      </Suspense>
                    </DndProvider>
                  </SnackbarProvider>
                </ThemeProvider>
              </Auth0Provider>
            </ActiveVideoVisitProvider>
          </BrowserRouter>
        </ApiProvider>
      </ReduxProvider>
    </ErrorBoundary>
  )
}

export default App
